import type { Matter } from "@/shared/cache";
import type {
	AccessPolicy,
	AccessPolicyDraft,
	AccessPolicyMode,
	AccessPolicyProfessional,
	ReplaceAccessPolicyBody,
} from "./types";

export type ClientOption = {
	code: string;
	name: string;
};

export type ProfessionalFilters = {
	status: "active" | "inactive" | "all";
	role: string;
	profType: string;
	department: string;
	practiceType: string;
	licensed: "licensed" | "unlicensed" | "all";
};

export const DEFAULT_PROFESSIONAL_FILTERS: ProfessionalFilters = {
	status: "active",
	role: "all",
	profType: "all",
	department: "all",
	practiceType: "all",
	licensed: "all",
};

export function matterScopeParts(matter: Matter): {
	clientCode: string;
	matterId: string;
} {
	const [clientCode = "", ...matterParts] = matter.matter_id.split(".");
	return {
		clientCode: clientCode.trim(),
		matterId: matterParts.join(".").trim(),
	};
}

export function clientOptionsFromMatters(matters: Matter[]): ClientOption[] {
	const clientsByCode = new Map<string, ClientOption>();

	for (const matter of matters) {
		const { clientCode } = matterScopeParts(matter);
		if (!clientCode || clientsByCode.has(clientCode)) continue;
		clientsByCode.set(clientCode, {
			code: clientCode,
			name: matter.client.trim(),
		});
	}

	return [...clientsByCode.values()].sort((left, right) =>
		left.code.localeCompare(right.code),
	);
}

export function professionalMatchesSearch(
	professional: AccessPolicyProfessional,
	query: string,
): boolean {
	const normalizedQuery = query.trim().toLowerCase();
	if (!normalizedQuery) return true;

	const searchableValues = [
		professional.name,
		professional.email,
		professional.profId,
		professional.role,
		professional.profType,
		professional.department,
		professional.practiceType,
		professional.isLicensed ? "licensed" : "unlicensed",
		professional.isActive ? "active" : "inactive",
	];

	return searchableValues.some((value) =>
		value?.toLowerCase().includes(normalizedQuery),
	);
}

export function professionalMatchesFilters(
	professional: AccessPolicyProfessional,
	filters: ProfessionalFilters,
): boolean {
	if (
		(filters.status === "active" && !professional.isActive) ||
		(filters.status === "inactive" && professional.isActive)
	) {
		return false;
	}
	if (filters.role !== "all" && professional.role !== filters.role) {
		return false;
	}
	if (
		filters.profType !== "all" &&
		professional.profType !== filters.profType
	) {
		return false;
	}
	if (
		filters.department !== "all" &&
		professional.department !== filters.department
	) {
		return false;
	}
	if (
		filters.practiceType !== "all" &&
		professional.practiceType !== filters.practiceType
	) {
		return false;
	}
	if (
		(filters.licensed === "licensed" && !professional.isLicensed) ||
		(filters.licensed === "unlicensed" && professional.isLicensed)
	) {
		return false;
	}
	return true;
}

export function uniqueProfessionalValues(
	professionals: AccessPolicyProfessional[],
	field: "role" | "profType" | "department" | "practiceType",
): string[] {
	return [
		...new Set(
			professionals
				.map((professional) => professional[field])
				.filter((value): value is string => Boolean(value?.trim())),
		),
	].sort((left, right) => left.localeCompare(right));
}

export function draftFromPolicy(policy: AccessPolicy): AccessPolicyDraft {
	return {
		mode: policy.mode,
		professionalIds: normalizeProfessionalIds(
			policy.professionals.map((professional) => professional.profId),
		),
	};
}

export function normalizeProfessionalIds(ids: Iterable<string>): string[] {
	return [...new Set(ids)].sort((left, right) => left.localeCompare(right));
}

export function toggleProfessional(
	draft: AccessPolicyDraft,
	professionalId: string,
): AccessPolicyDraft {
	const nextIds = new Set(draft.professionalIds);
	if (nextIds.has(professionalId)) {
		nextIds.delete(professionalId);
	} else {
		nextIds.add(professionalId);
	}

	return {
		...draft,
		professionalIds: normalizeProfessionalIds(nextIds),
	};
}

export function selectProfessionals(
	draft: AccessPolicyDraft,
	professionalIds: Iterable<string>,
): AccessPolicyDraft {
	return {
		...draft,
		professionalIds: normalizeProfessionalIds([
			...draft.professionalIds,
			...professionalIds,
		]),
	};
}

export function deselectProfessionals(
	draft: AccessPolicyDraft,
	professionalIds: Iterable<string>,
): AccessPolicyDraft {
	const idsToRemove = new Set(professionalIds);
	return {
		...draft,
		professionalIds: draft.professionalIds.filter(
			(professionalId) => !idsToRemove.has(professionalId),
		),
	};
}

export function updateDraftMode(
	draft: AccessPolicyDraft,
	mode: AccessPolicyMode,
): AccessPolicyDraft {
	return { ...draft, mode };
}

export function hasDraftChanges(
	policy: AccessPolicy | null,
	draft: AccessPolicyDraft,
): boolean {
	if (!policy || policy.mode !== draft.mode) return true;

	const savedIds = normalizeProfessionalIds(
		policy.professionals.map((professional) => professional.profId),
	);
	const draftIds = normalizeProfessionalIds(draft.professionalIds);
	return (
		savedIds.length !== draftIds.length ||
		savedIds.some((id, index) => id !== draftIds[index])
	);
}

export function buildReplaceAccessPolicyBody(
	clientCode: string,
	matterId: string | null,
	draft: AccessPolicyDraft,
): ReplaceAccessPolicyBody {
	return {
		clientCode,
		matterId,
		mode: draft.mode,
		professionalIds: normalizeProfessionalIds(draft.professionalIds),
	};
}
